import React, { useState } from "react";
import Typography from "../typography";
import { styled, Card, CardContent, Box } from "@material-ui/core";
import { IStyledArguments } from "../../types/styled-arguments";
import { injectIntl, FormattedMessage, InjectedIntlProps } from "react-intl";
import Select from "../select";
import Option from "../select/option";
import messages from "./PageNewPlayer.intl";
import { gql, useMutation } from "@apollo/client";
import Link from "../link";
import Button from "../button";
import { ITeam } from "../../types";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import PersonIcon from "@material-ui/icons/Person";
import SportsIcon from "@material-ui/icons/Sports";
import GroupIcon from "@material-ui/icons/Group";
import VisibilityIcon from "@material-ui/icons/Visibility";

const ADD_PLAYER_TO_TEAM = gql`
  mutation AddPlayerTeam($playerId: ID!, $teamId: ID, $scouting: Boolean) {
    addPlayerToTeam(playerId: $playerId, teamId: $teamId, scouting: $scouting) {
      id
      name
    }
  }
`;

export interface IStepInitialProps {
  onGoNext: (values?: any) => void;
  userDetails: any;
  teams: any;
}

const Title = styled("div")(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    "& h5": {
      margin: 0,
      fontWeight: 700,
      fontSize: "1.75rem",
    },
    "& svg": {
      fontSize: "2rem",
    },
    [`@media (max-width: ${md}px)`]: {
      marginBottom: theme.spacing(2),
      "& h5": {
        fontSize: "1.5rem",
      },
      "& svg": {
        fontSize: "1.75rem",
      },
    },
  };
});

const ContentCard = styled(Card)(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.extra.borderColor}`,
    boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
    marginBottom: theme.spacing(3),
    [`@media (max-width: ${md}px)`]: {
      borderRadius: 12,
      marginBottom: theme.spacing(2),
    },
  };
});

const PlayerInfoSection = styled(Box)(({ theme }: IStyledArguments) => {
  return {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: theme.palette.extra.accentSoft,
    borderRadius: 12,
    border: `1px solid ${theme.palette.extra.borderColor}`,
  };
});

const PlayerAvatar = styled("div")(({ theme }: IStyledArguments) => {
  return {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: "2rem",
      color: "#fff",
    },
  };
});

const PlayerDetails = styled("div")(({ theme }: IStyledArguments) => {
  return {
    flex: 1,
    "& h6": {
      margin: 0,
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: "1.1rem",
    },
    "& .position": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      fontSize: "0.95rem",
      "& svg": {
        fontSize: "1.2rem",
      },
    },
  };
});

const SectionDivider = styled("div")(({ theme }: IStyledArguments) => {
  return {
    height: 1,
    background: "linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)",
    margin: theme.spacing(3, 0),
  };
});

const OptionSection = styled("div")(({ theme }: IStyledArguments) => {
  return {
    marginBottom: theme.spacing(3),
  };
});

const SectionLabel = styled("div")(({ theme }: IStyledArguments) => {
  return {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "1rem",
    "& svg": {
      fontSize: "1.5rem",
    },
  };
});

const StyledSwitch = styled(FormControlLabel)(({ theme }: IStyledArguments) => {
  return {
    marginBottom: theme.spacing(2),
    "& .MuiFormControlLabel-label": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  };
});

const StyledActionLink = styled(Link)(({ theme }: IStyledArguments) => {
  return {
    ...theme.typography.caption,
    textDecoration: "none",
    display: "block",
    width: "100%",
  };
});

const ButtonRow = styled("div")(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(3),
    [`@media (max-width: ${md}px)`]: {
      flexDirection: "column-reverse",
      gap: theme.spacing(1.5),
    },
  };
});

const StyledButton = styled(Button)(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    padding: theme.spacing(1.5, 4),
    borderRadius: 12,
    fontWeight: 600,
    fontSize: "1rem",
    textTransform: "none",
    transition: "all 0.3s ease",
    [`@media (max-width: ${md}px)`]: {
      width: "100%",
      padding: theme.spacing(1.25, 3),
    },
  };
});

const PrimaryButton = styled(StyledButton)(({ theme }: IStyledArguments) => {
  return {
    background: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      background: theme.palette.primary.dark,
    },
  };
});

const SecondaryButton = styled(StyledButton)(({ theme }: IStyledArguments) => {
  return {
    background: "rgba(0, 0, 0, 0.04)",
    color: theme.palette.text.secondary,
    "&:hover": {
      background: "rgba(0, 0, 0, 0.08)",
    },
  };
});

const TeamsSelect = injectIntl<{
  onChange: (event: any) => void;
  value: any;
  teams: ITeam[];
  intl: InjectedIntlProps;
}>(({ onChange, value, teams }) => {
  return (
    <Select
      id="team-select"
      label="Team"
      value={value}
      onChange={onChange}
      fullWidth
    >
      <Option value="">
        <em>{"Select a Team"}</em>
      </Option>
      {teams.map(({ id, name }, idx: number) => (
        <Option key={idx} value={id}>
          {name}
        </Option>
      ))}
    </Select>
  );
});

const StepAddToTeam = ({ onGoNext, userDetails, teams }: IStepInitialProps) => {
  const [team, setTeam] = useState("");
  const [addToTeam] = useMutation(ADD_PLAYER_TO_TEAM);
  const [isScouting, setIsScouting] = useState<boolean>(false);

  const handleSave = async () => {
    await addToTeam({
      variables: {
        playerId: userDetails.id,
        teamId: team,
        scouting: isScouting,
      },
    });
    onGoNext();
  };

  const handleChangeIsScouting = () => {
    setIsScouting(!isScouting);
  };

  return (
    <>
      <Title>
        <GroupAddIcon />
        <Typography.H5>
          <FormattedMessage {...messages.addToTeamTitle} />
        </Typography.H5>
      </Title>

      <ContentCard>
        <CardContent>
          <PlayerInfoSection>
            <PlayerAvatar>
              <PersonIcon />
            </PlayerAvatar>
            <PlayerDetails>
              <Typography.H6>
                {userDetails.name}&nbsp;{userDetails.surname}
              </Typography.H6>
              <div className="position">
                <SportsIcon />
                <span>{userDetails.position}</span>
              </div>
            </PlayerDetails>
          </PlayerInfoSection>

          <SectionDivider />

          <OptionSection>
            <SectionLabel>
              <VisibilityIcon />
              <span>Assignment Type</span>
            </SectionLabel>
            <StyledSwitch
              control={
                <Switch
                  checked={isScouting}
                  onChange={handleChangeIsScouting}
                  name="scoutingMode"
                  color="primary"
                />
              }
              label={
                <span>
                  <VisibilityIcon style={{ fontSize: "1.2rem" }} />
                  No team (Scouting)
                </span>
              }
            />
          </OptionSection>

          {!isScouting && (
            <OptionSection>
              <SectionLabel>
                <GroupIcon />
                <span>Select Team</span>
              </SectionLabel>
              <TeamsSelect
                teams={teams}
                value={team}
                onChange={(event: any) => {
                  setTeam(event.target.value);
                }}
              />
            </OptionSection>
          )}
        </CardContent>
      </ContentCard>

      <ButtonRow>
        <StyledActionLink to="/people/players">
          <SecondaryButton>Skip</SecondaryButton>
        </StyledActionLink>
        <PrimaryButton onClick={handleSave}>Save & Continue</PrimaryButton>
      </ButtonRow>
    </>
  );
};

export default StepAddToTeam;
